import { memo, useCallback, useEffect, useRef, useState } from "react";

import { EditOutlined } from "@ant-design/icons";
import { connect, mapProps, mapReadPretty, useField } from "@formily/react";
import { Button, Flex, Modal, Space, Typography } from "antd";

import { getToken, parseJwtPayload, useUserStore } from "@neu/libui";

import styles from "./EsignField.module.scss";

export interface EsignValue {
	userName: string;
	userId: number;
	signSvg: string;
	signedAt: string;
	location?: string | null;
}

interface LegacyEsignValue {
	image?: {
		png?: string;
		svg?: string;
	};
	name?: string;
	timestamp?: number | string;
}

type StoredEsignValue = EsignValue | LegacyEsignValue;

interface EsignFieldProps {
	value?: StoredEsignValue | null;
	onChange?: (value: EsignValue | undefined) => void;
	disabled?: boolean;
	readOnly?: boolean;
}

function stripDataUrlPrefix(data: string): string {
	const match = data.match(/^data:[^;]+;base64,(.+)$/);
	return match?.[1] ?? data;
}

function toSignatureImageSrc(signSvg: string): string {
	const trimmed = signSvg.trim();
	if (trimmed.startsWith("data:")) {
		return trimmed;
	}
	return `data:image/png;base64,${trimmed}`;
}

function normalizeSignedAt(
	timestamp: number | string | undefined
): string | undefined {
	if (timestamp === undefined || timestamp === null) {
		return undefined;
	}
	if (typeof timestamp === "number") {
		return new Date(timestamp).toISOString();
	}
	const trimmed = timestamp.trim();
	if (!trimmed) {
		return undefined;
	}
	if (/^\d+$/.test(trimmed)) {
		return new Date(Number(trimmed)).toISOString();
	}
	return trimmed;
}

function normalizeEsignValue(
	value: StoredEsignValue | null | undefined
): EsignValue | null | undefined {
	if (value === null || value === undefined) {
		return value;
	}

	const record = value as LegacyEsignValue & Partial<EsignValue>;

	if (
		typeof record.userName === "string" &&
		typeof record.userId === "number" &&
		typeof record.signSvg === "string" &&
		record.signSvg.length > 0 &&
		typeof record.signedAt === "string"
	) {
		return {
			userId: record.userId,
			userName: record.userName,
			signSvg: record.signSvg,
			signedAt: record.signedAt,
			location: record.location ?? null,
		};
	}

	const image =
		record.image &&
		typeof record.image === "object" &&
		!Array.isArray(record.image)
			? record.image
			: undefined;
	const rawImage = image?.png ?? image?.svg;

	const userName =
		typeof record.userName === "string"
			? record.userName
			: typeof record.name === "string"
				? record.name
				: undefined;

	const signedAt =
		typeof record.signedAt === "string"
			? record.signedAt
			: normalizeSignedAt(record.timestamp);

	const signSvg =
		typeof record.signSvg === "string" && record.signSvg.trim()
			? stripDataUrlPrefix(record.signSvg)
			: typeof rawImage === "string" && rawImage.trim()
				? stripDataUrlPrefix(rawImage)
				: undefined;

	const userId =
		typeof record.userId === "number"
			? record.userId
			: typeof record.userId === "string" && /^\d+$/.test(record.userId)
				? Number(record.userId)
				: 0;

	if (!userName || !signSvg || !signedAt) {
		return undefined;
	}

	return {
		userId,
		userName,
		signSvg,
		signedAt,
		location:
			typeof record.location === "string" ? record.location : null,
	};
}

function getSignerInfo(): { userID: number; userName: string } {
	const { profile } = useUserStore.getState();

	if (profile?.id !== null && profile?.id !== undefined && profile.username) {
		return { userID: profile.id, userName: profile.username };
	}

	const token = getToken();
	if (!token) return { userID: 0, userName: "Unknown" };

	const payload = parseJwtPayload(token);
	if (!payload) return { userID: 0, userName: "Unknown" };

	const userID = typeof payload.userID === "number" ? payload.userID : 0;
	const userName =
		typeof payload.username === "string"
			? payload.username
			: typeof payload.email === "string"
				? payload.email
				: "Unknown";

	return { userID, userName };
}

function getLocation(): Promise<string | null> {
	return new Promise((resolve) => {
		if (!navigator.geolocation) {
			resolve(null);
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				resolve(
					`${pos.coords.latitude.toFixed(6)},${pos.coords.longitude.toFixed(6)}`
				);
			},
			() => {
				resolve(null);
			},
			{ timeout: 5000, maximumAge: 60000 }
		);
	});
}

function isSigned(
	value: StoredEsignValue | null | undefined
): value is EsignValue {
	const normalized = normalizeEsignValue(value);
	return normalized != null && normalized.signSvg.length > 0;
}

function isCanvasBlank(canvas: HTMLCanvasElement): boolean {
	const ctx = canvas.getContext("2d");
	if (!ctx) return true;
	const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
	for (let i = 3; i < data.length; i += 4) {
		if (data[i] !== 0) return false;
	}
	return true;
}

const SignatureCanvas = memo(
	({
		canvasRef,
		onDraw,
	}: {
		canvasRef: React.RefObject<HTMLCanvasElement | null>;
		onDraw: () => void;
	}) => {
		const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
		const drawingRef = useRef(false);

		useEffect(() => {
			const canvas = canvasRef.current;
			let observer: ResizeObserver | undefined;
			let lastWidth = 0;
			let lastHeight = 0;

			if (canvas) {
				const setupCanvas = () => {
					const ctx = canvas.getContext("2d");
					if (!ctx) return;

					const dpr = window.devicePixelRatio ?? 1;
					const rect = canvas.getBoundingClientRect();
					const nextWidth = Math.max(1, Math.round(rect.width * dpr));
					const nextHeight = Math.max(
						1,
						Math.round(rect.height * dpr)
					);

					// Avoid wiping strokes when size has not actually changed.
					if (nextWidth === lastWidth && nextHeight === lastHeight) {
						return;
					}

					lastWidth = nextWidth;
					lastHeight = nextHeight;
					canvas.width = nextWidth;
					canvas.height = nextHeight;
					ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
					ctx.strokeStyle = "#000";
					ctx.lineWidth = 2;
					ctx.lineCap = "round";
					ctx.lineJoin = "round";
					ctxRef.current = ctx;
				};

				setupCanvas();

				observer = new ResizeObserver(() => {
					setupCanvas();
				});
				observer.observe(canvas);
			}

			return () => {
				observer?.disconnect();
			};
		}, [canvasRef]);

		const getPointerPos = useCallback(
			(e: React.MouseEvent | React.TouchEvent) => {
				const canvas = canvasRef.current;
				if (!canvas) return null;
				const rect = canvas.getBoundingClientRect();
				const clientX =
					"touches" in e ? e.touches[0]?.clientX : e.clientX;
				const clientY =
					"touches" in e ? e.touches[0]?.clientY : e.clientY;
				if (
					clientX === null ||
					clientX === undefined ||
					clientY === null ||
					clientY === undefined
				) {
					return null;
				}
				return { x: clientX - rect.left, y: clientY - rect.top };
			},
			[canvasRef]
		);

		const start = useCallback(
			(e: React.MouseEvent | React.TouchEvent) => {
				e.preventDefault();
				const ctx = ctxRef.current;
				const pos = getPointerPos(e);
				if (!ctx || !pos) return;
				drawingRef.current = true;
				ctx.beginPath();
				ctx.moveTo(pos.x, pos.y);
			},
			[getPointerPos]
		);

		const move = useCallback(
			(e: React.MouseEvent | React.TouchEvent) => {
				e.preventDefault();
				if (!drawingRef.current) return;
				const ctx = ctxRef.current;
				const pos = getPointerPos(e);
				if (!ctx || !pos) return;
				ctx.lineTo(pos.x, pos.y);
				ctx.stroke();
				onDraw();
			},
			[getPointerPos, onDraw]
		);

		const end = useCallback(() => {
			drawingRef.current = false;
		}, []);

		return (
			<canvas
				className={styles.canvas}
				onMouseDown={start}
				onMouseLeave={end}
				onMouseMove={move}
				onMouseUp={end}
				onTouchEnd={end}
				onTouchMove={move}
				onTouchStart={start}
				ref={canvasRef}
			/>
		);
	}
);

SignatureCanvas.displayName = "SignatureCanvas";

const EsignFieldInner = memo((props: EsignFieldProps) => {
	const { value, onChange, disabled, readOnly } = props;
	const field = useField();
	const isReadOnly =
		disabled ?? readOnly ?? field.disabled ?? field.readOnly ?? false;
	const [modalOpen, setModalOpen] = useState(false);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [hasDrawn, setHasDrawn] = useState(false);

	const normalizedValue = normalizeEsignValue(value);
	const signed = isSigned(normalizedValue);

	const openModal = useCallback(() => {
		if (isReadOnly) return;
		setModalOpen(true);
		setHasDrawn(false);
	}, [isReadOnly]);

	const closeModal = useCallback(() => {
		setModalOpen(false);
	}, []);

	const clearCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.save();
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.restore();
		setHasDrawn(false);
	}, []);

	const confirmSign = useCallback(async () => {
		const canvas = canvasRef.current;
		if (!canvas || !onChange) return;
		if (isCanvasBlank(canvas)) return;

		const dataURL = canvas.toDataURL("image/png");
		const [, base64] = dataURL.split(",");
		if (!base64) return;

		const { userID, userName } = getSignerInfo();
		const location = await getLocation();

		onChange({
			userId: userID,
			userName,
			signSvg: base64,
			signedAt: new Date().toISOString(),
			location,
		});
		closeModal();
	}, [onChange, closeModal]);

	const handleClear = useCallback(() => {
		onChange?.(undefined);
	}, [onChange]);

	return (
		<>
			<Flex gap={8} vertical>
				{signed && normalizedValue && (
					<>
						<Flex align="center" gap={12} wrap="wrap">
							{normalizedValue.signSvg && (
								<img
									alt="Signature"
									className={styles.thumbnail}
									src={toSignatureImageSrc(
										normalizedValue.signSvg
									)}
								/>
							)}
							<Flex vertical>
								<Typography.Text strong>
									Signed by {normalizedValue.userName}
								</Typography.Text>
								<Typography.Text type="secondary">
									{new Date(
										normalizedValue.signedAt
									).toLocaleString()}
									{normalizedValue.location
										? ` · ${normalizedValue.location}`
										: ""}
								</Typography.Text>
							</Flex>
						</Flex>
						{!isReadOnly && (
							<Space>
								<Button
									icon={<EditOutlined />}
									onClick={openModal}
									size="small"
								>
									Re-sign
								</Button>
								<Button
									onClick={handleClear}
									size="small"
									type="text"
								>
									Clear
								</Button>
							</Space>
						)}
					</>
				)}
				{!signed && (
					<Flex align="center" gap={8}>
						<Typography.Text type="secondary">
							Not signed
						</Typography.Text>
						{!isReadOnly && (
							<Button
								onClick={openModal}
								size="small"
								type="primary"
							>
								Click to sign
							</Button>
						)}
					</Flex>
				)}
			</Flex>
			<Modal
				centered
				className={`compactConfirmModal ${styles.signModal}`}
				destroyOnClose
				okButtonProps={{ disabled: !hasDrawn }}
				okText="Sign"
				onCancel={closeModal}
				onOk={confirmSign}
				open={modalOpen}
				title="Sign"
				width={400}
			>
				<Flex className={styles.modalBody} vertical>
					<Typography.Text
						className={styles.modalHint}
						type="secondary"
					>
						Draw your signature below.
					</Typography.Text>
					<SignatureCanvas
						canvasRef={canvasRef}
						onDraw={() => {
							setHasDrawn(true);
						}}
					/>
					<Flex className={styles.clearRow} justify="end">
						<Button onClick={clearCanvas} size="small">
							Clear
						</Button>
					</Flex>
				</Flex>
			</Modal>
		</>
	);
});

EsignFieldInner.displayName = "EsignFieldInner";

const EsignFieldReadPretty = memo((props: EsignFieldProps) => {
	const { value } = props;
	const normalizedValue = normalizeEsignValue(value);
	const signed = isSigned(normalizedValue);

	return (
		<>
			{!signed && (
				<Typography.Text type="secondary">—</Typography.Text>
			)}
			{signed && normalizedValue && (
				<Flex align="center" gap={12}>
					{normalizedValue.signSvg && (
						<img
							alt="Signature"
							className={styles.thumbnailReadOnly}
							src={toSignatureImageSrc(normalizedValue.signSvg)}
						/>
					)}
					<Typography.Text>
						{normalizedValue.userName} ·{" "}
						{new Date(normalizedValue.signedAt).toLocaleString()}
					</Typography.Text>
				</Flex>
			)}
		</>
	);
});

EsignFieldReadPretty.displayName = "EsignFieldReadPretty";

export const EsignField = connect(
	EsignFieldInner,
	mapProps((props: EsignFieldProps) => ({ ...props })),
	mapReadPretty(EsignFieldReadPretty)
);
